import type { inferRouterOutputs } from "@trpc/server";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Link } from "react-router";

import type { AppRouter } from "@runcheetah/api/router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@runcheetah/ui";
import { CodeText, InfoTile, PageHeader, QueryErrorState } from "@/components/common";
import { getWorkspaceBillingStatusMeta } from "@/lib/billing-status";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type PaymentsOverview = RouterOutputs["admin"]["payments"]["overview"];
type PaymentSubscription = RouterOutputs["admin"]["payments"]["subscriptions"][number];
type PaymentInvoiceList = RouterOutputs["admin"]["payments"]["invoices"];

const formatMoneyEur = (cents: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(cents / 100);

const formatDate = (value: string | null): string => (value ? formatDateTime(value) : "Not set");

function StatusBadge({ status }: { status: PaymentSubscription["status"] }) {
  const statusMeta = getWorkspaceBillingStatusMeta(status);

  return (
    <span
      className={cn(
        "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
        statusMeta.className,
      )}
    >
      {statusMeta.label}
    </span>
  );
}

function StripeObjectLink({ id, url }: { id: string | null; url: string | null }) {
  if (!id) {
    return <span className="text-muted-foreground">Missing</span>;
  }

  if (!url) {
    return <CodeText>{id}</CodeText>;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-xs font-medium text-foreground hover:underline"
    >
      <CodeText>{id}</CodeText>
      <ExternalLink className="size-3" />
    </a>
  );
}

function PaymentsHealthCards({ overview }: { overview: PaymentsOverview }) {
  const healthLabel = overview.health.stripeConfigured ? "Stripe connected" : "Stripe disabled";
  const webhookLabel = overview.health.webhookConfigured ? "Webhook configured" : "Webhook missing";

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
      <InfoTile label="Stripe" value={healthLabel} />
      <InfoTile label="Webhook" value={webhookLabel} />
      <InfoTile label="Mode" value={overview.health.mode ?? "Not configured"} />
      <InfoTile label="MRR" value={formatMoneyEur(overview.revenue.mrrCents)} />
      <InfoTile label="ARR" value={formatMoneyEur(overview.revenue.arrCents)} />
    </div>
  );
}

function PaymentsPlanCard({ overview }: { overview: PaymentsOverview }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle>Plan catalog</CardTitle>
          <p className="text-sm text-muted-foreground">Local plan setup and Stripe sync status.</p>
        </div>
        <Link to="/billing-plans" className="text-sm font-medium hover:underline">
          Manage plans
        </Link>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-4">
        <InfoTile label="Plans" value={String(overview.plans.total)} />
        <InfoTile label="Active" value={String(overview.plans.active)} />
        <InfoTile label="Stripe-backed" value={String(overview.plans.stripe)} />
        <InfoTile label="Sync failures" value={String(overview.health.failedStripePlanCount)} />
      </CardContent>
    </Card>
  );
}

function SubscriptionStatusCard({ overview }: { overview: PaymentsOverview }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Subscription status</CardTitle>
        <p className="text-sm text-muted-foreground">
          Local workspace billing accounts grouped by Stripe subscription state.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
          <InfoTile label="Total" value={String(overview.subscriptions.total)} />
          <InfoTile label="Canceling" value={String(overview.subscriptions.cancelAtPeriodEnd)} />
          <InfoTile
            label="Pending changes"
            value={String(overview.subscriptions.pendingPlanChanges)}
          />
          <InfoTile
            label="Missing Stripe IDs"
            value={String(
              overview.subscriptions.missingStripeCustomer +
                overview.subscriptions.missingStripeSubscription,
            )}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {overview.subscriptions.statusCounts
            .filter((item) => item.count > 0)
            .map((item) => {
              const statusMeta = getWorkspaceBillingStatusMeta(item.status);
              return (
                <span
                  key={item.status}
                  className={cn(
                    "inline-flex rounded-full border px-2 py-1 text-xs font-medium",
                    statusMeta.className,
                  )}
                >
                  {statusMeta.label}: {item.count}
                </span>
              );
            })}
        </div>
      </CardContent>
    </Card>
  );
}

function AttentionCard({ overview }: { overview: PaymentsOverview }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Needs attention</CardTitle>
        <p className="text-sm text-muted-foreground">
          Failed syncs, risky subscription states, missing Stripe links, and pending changes.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {overview.attentionItems.length ? null : (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            No billing issues found.
          </div>
        )}
        {overview.attentionItems.map((item, index) => (
          <div
            key={`${item.kind}-${item.workspaceId ?? item.planId}-${index}`}
            className="rounded-lg border p-3"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                className={cn(
                  "mt-0.5 size-4",
                  item.severity === "critical" ? "text-red-600" : "text-amber-600",
                )}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{item.message}</div>
                <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {item.workspaceId ? (
                    <Link to={`/workspaces/${item.workspaceId}`} className="hover:underline">
                      {item.workspaceName}
                    </Link>
                  ) : null}
                  {item.planName ? <span>{item.planName}</span> : null}
                  <Badge variant="outline">{item.severity}</Badge>
                </div>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function SubscriptionsTable({ subscriptions }: { subscriptions: PaymentSubscription[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Workspace subscriptions</CardTitle>
        <p className="text-sm text-muted-foreground">
          Local subscription state with Stripe object links for reconciliation.
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-left text-sm">
          <thead className="border-b text-xs text-muted-foreground uppercase">
            <tr>
              <th className="px-3 py-2 font-medium">Workspace</th>
              <th className="px-3 py-2 font-medium">Plan</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Revenue</th>
              <th className="px-3 py-2 font-medium">Period</th>
              <th className="px-3 py-2 font-medium">Credits</th>
              <th className="px-3 py-2 font-medium">Stripe</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {subscriptions.map((subscription) => (
              <tr key={subscription.billingAccountId}>
                <td className="px-3 py-3 align-top">
                  <Link
                    to={`/workspaces/${subscription.workspaceId}`}
                    className="font-medium hover:underline"
                  >
                    {subscription.workspaceName}
                  </Link>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {subscription.ownerEmail}
                  </div>
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="font-medium">{subscription.planName}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {subscription.billingCycle} · {subscription.planCode}
                  </div>
                  {subscription.pendingPlanName ? (
                    <div className="mt-1 text-xs text-amber-700">
                      Pending: {subscription.pendingPlanName} ({subscription.pendingBillingCycle})
                    </div>
                  ) : null}
                </td>
                <td className="px-3 py-3 align-top">
                  <StatusBadge status={subscription.status} />
                  {subscription.cancelAtPeriodEnd ? (
                    <div className="mt-1 text-xs text-amber-700">Canceling</div>
                  ) : null}
                </td>
                <td className="px-3 py-3 align-top font-medium">
                  {formatMoneyEur(subscription.monthlyRevenueCents)}/mo
                </td>
                <td className="px-3 py-3 align-top text-xs text-muted-foreground">
                  <div>{formatDate(subscription.currentPeriodStart)}</div>
                  <div>{formatDate(subscription.currentPeriodEnd)}</div>
                </td>
                <td className="px-3 py-3 align-top text-xs text-muted-foreground">
                  {subscription.creditUsage.used.toLocaleString()} /{" "}
                  {subscription.creditUsage.total?.toLocaleString() ?? "unlimited"}
                </td>
                <td className="space-y-1 px-3 py-3 align-top">
                  <div>
                    <span className="mr-1 text-xs text-muted-foreground">Customer</span>
                    <StripeObjectLink
                      id={subscription.stripeCustomerId}
                      url={subscription.stripeCustomerUrl}
                    />
                  </div>
                  <div>
                    <span className="mr-1 text-xs text-muted-foreground">Sub</span>
                    <StripeObjectLink
                      id={subscription.stripeSubscriptionId}
                      url={subscription.stripeSubscriptionUrl}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {subscriptions.length ? null : (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            No workspace subscriptions found.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InvoicesCard({ invoiceList }: { invoiceList: PaymentInvoiceList | undefined }) {
  if (!invoiceList) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Stripe invoices</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-28 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!invoiceList.enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Stripe invoices</CardTitle>
        </CardHeader>
        <CardContent className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          Stripe invoices are unavailable because `STRIPE_SECRET_KEY` is not configured.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recent Stripe invoices</CardTitle>
        <p className="text-sm text-muted-foreground">
          Live Stripe invoice snapshot. Local workspace names appear when customers are linked.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 overflow-x-auto">
        {invoiceList.error ? (
          <div className="text-sm text-destructive">{invoiceList.error}</div>
        ) : null}
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="border-b text-xs text-muted-foreground uppercase">
            <tr>
              <th className="px-3 py-2 font-medium">Invoice</th>
              <th className="px-3 py-2 font-medium">Workspace</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Amount</th>
              <th className="px-3 py-2 font-medium">Created</th>
              <th className="px-3 py-2 font-medium">Links</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {invoiceList.invoices.map((invoice) => (
              <tr key={invoice.id}>
                <td className="px-3 py-3 align-top">
                  <StripeObjectLink id={invoice.id} url={null} />
                  {invoice.number ? (
                    <div className="mt-1 text-xs text-muted-foreground">{invoice.number}</div>
                  ) : null}
                </td>
                <td className="px-3 py-3 align-top">
                  {invoice.workspaceId ? (
                    <Link
                      to={`/workspaces/${invoice.workspaceId}`}
                      className="font-medium hover:underline"
                    >
                      {invoice.workspaceName}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">Unlinked</span>
                  )}
                </td>
                <td className="px-3 py-3 align-top capitalize">{invoice.status}</td>
                <td className="px-3 py-3 align-top font-medium">
                  {formatMoneyEur(invoice.amountPaidCents)}
                </td>
                <td className="px-3 py-3 align-top text-muted-foreground">
                  {formatDate(invoice.createdAt)}
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex flex-wrap gap-2">
                    {invoice.hostedInvoiceUrl ? (
                      <a
                        href={invoice.hostedInvoiceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        Hosted
                      </a>
                    ) : null}
                    {invoice.pdfUrl ? (
                      <a
                        href={invoice.pdfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        PDF
                      </a>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invoiceList.invoices.length ? null : (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            No recent Stripe invoices returned.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PaymentsPage() {
  const overviewQuery = trpc.admin.payments.overview.useQuery();
  const subscriptionsQuery = trpc.admin.payments.subscriptions.useQuery();
  const invoicesQuery = trpc.admin.payments.invoices.useQuery(undefined, { staleTime: 60_000 });

  if (overviewQuery.isPending || subscriptionsQuery.isPending) {
    return (
      <div className="space-y-4">
        <PageHeader title="Payments" meta="Loading..." />
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  const error = overviewQuery.error ?? subscriptionsQuery.error;
  if (error || !overviewQuery.data || !subscriptionsQuery.data) {
    return (
      <QueryErrorState
        title="Failed to load payments overview"
        message={error?.message ?? "Payments data was unavailable."}
        isForbidden={error?.data?.code === "FORBIDDEN"}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments"
        meta={`${overviewQuery.data.subscriptions.total} subscriptions`}
        description="Operational view of Stripe readiness, local billing state, revenue, subscriptions, and recent invoices."
      />
      <PaymentsHealthCards overview={overviewQuery.data} />
      <div className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="space-y-6">
          <PaymentsPlanCard overview={overviewQuery.data} />
          <SubscriptionStatusCard overview={overviewQuery.data} />
        </div>
        <AttentionCard overview={overviewQuery.data} />
      </div>
      <SubscriptionsTable subscriptions={subscriptionsQuery.data} />
      <InvoicesCard invoiceList={invoicesQuery.data} />
    </div>
  );
}
